// TelcoTrack — Akviziční report (vstupní data projektu) → mapování na doménu.
//
// Jediné místo pravdy pro:
//   • normalizaci hlaviček CSV/XLSM (diakritika, velikost písmen, mezery),
//   • mapování sloupců reportu na kanonická pole, která aplikace zpracovává,
//   • odvození navrhovaného BUDOVA workflow ze „Status ACQ“,
//   • odvození stylu výstavby VR (horizontální propoj) z „Způsob … technického řešení BD“.
//
// Zdroj sloupců: docs/infrastructure/csv/Akviziční_report.csv (cp1250, oddělovač „;“).

import {
  AKVIZICE_DRAWING_REJECT_BUILDING_TYPE,
  AKVIZICE_DRAWING_SIGN_BUILDING_TYPE,
  type BudovaBuildingType,
} from './object-workflow/budova-building-type.constants';

/**
 * Normalizace textu pro porovnání (hlavičky i hodnoty): bez diakritiky, malá písmena,
 * sjednocené mezery. Funguje i na „mojibake“ z cp1250, protože ho jen normalizuje na
 * porovnatelný tvar — primárně se ale spoléháme na správně dekódovaný vstup.
 */
export function normalizeAcquisitionText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Kanonická pole reportu, která aplikace aktivně zpracovává (zbytek se ukládá do raw). */
export interface AcquisitionReportFields {
  /** AQCID — identifikátor řádku reportu (idempotentní re-import). */
  aqcId?: string;
  statusAcq?: string;
  souhlasSmlouva?: string;
  duvodNesouhlasu?: string;
  hpCount?: number;
  bjCount?: number;
  komerceCount?: number;
  rezervCount?: number;
  floorCount?: number;
  typDomu?: string;
  techReseniBd?: string;
  zpusobPripojeni?: string;
  parcelNumber?: string;
  katastralniUzemi?: string;
  cisloPopisne?: string;
  cisloOrientacniA?: string;
  ulice?: string;
  mesto?: string;
  ropId?: string;
  ownerType?: string;
  ownerName?: string;
  ico?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
}

/**
 * Mapa kanonické pole → seznam možných hlaviček (normalizovaných). Více variant kvůli
 * drobným odchylkám v reportech. Porovnává se přes `normalizeAcquisitionText`.
 */
export const ACQUISITION_HEADER_ALIASES: Record<keyof AcquisitionReportFields, string[]> = {
  aqcId: ['aqcid'],
  statusAcq: ['status acq'],
  souhlasSmlouva: ['souhlas_smlouva', 'souhlas smlouva'],
  duvodNesouhlasu: ['duvod nesouhlasu/duvod vraceni akvizice', 'duvod nesouhlasu'],
  hpCount: ['pocet hp - zadani', 'pocet hp zadani', 'pocet hp'],
  bjCount: ['pocet bj'],
  komerceCount: ['pocet komerce'],
  rezervCount: ['pocet rezerv'],
  floorCount: ['pocet pater'],
  typDomu: ['typ domu'],
  techReseniBd: ['zpusob projednavaneho technickeho reseni bd', 'zpusob projednavaneho technickeho reseni'],
  zpusobPripojeni: ['zpusob pripojeni objektu (bd/business)', 'zpusob pripojeni objektu'],
  parcelNumber: ['parcelni cislo'],
  katastralniUzemi: ['katastralni uzemi'],
  cisloPopisne: ['cislo popisne'],
  cisloOrientacniA: ['cislo orientacni a', 'cislo orientacni'],
  ulice: ['ulice'],
  mesto: ['mesto', 'obec'],
  ropId: ['rop id'],
  ownerType: ['typ vlastnika/spravy', 'typ vlastnika'],
  ownerName: ['jmeno/prijmeni/nazev vlastnika/firmy', 'nazev vlastnika/firmy'],
  ico: ['ico'],
  contactPerson: ['kontaktni osoba (jmeno a prijmeni)', 'kontaktni osoba'],
  contactEmail: ['kontakt mail'],
  contactPhone: ['kontakt telefon'],
};

/**
 * Čitelné (CZ) názvy kanonických sloupců reportu pro zobrazení v UI (editovatelná mřížka
 * v záložce „Objekty k připojení“). Pořadí klíčů = pořadí sloupců v mřížce.
 */
export const ACQUISITION_FIELD_LABELS: Record<keyof AcquisitionReportFields, string> = {
  aqcId: 'AQCID',
  statusAcq: 'Status ACQ',
  souhlasSmlouva: 'Souhlas / smlouva',
  duvodNesouhlasu: 'Důvod nesouhlasu',
  hpCount: 'Počet HP',
  bjCount: 'Počet BJ',
  komerceCount: 'Počet komerce',
  rezervCount: 'Počet rezerv',
  floorCount: 'Počet pater',
  typDomu: 'Typ domu',
  techReseniBd: 'Technické řešení BD',
  zpusobPripojeni: 'Způsob připojení',
  parcelNumber: 'Parcelní číslo',
  katastralniUzemi: 'Katastrální území',
  cisloPopisne: 'Číslo popisné',
  cisloOrientacniA: 'Číslo orientační',
  ulice: 'Ulice',
  mesto: 'Město',
  ropId: 'ROP ID',
  ownerType: 'Typ vlastníka / správy',
  ownerName: 'Vlastník / firma',
  ico: 'IČO',
  contactPerson: 'Kontaktní osoba',
  contactEmail: 'Kontakt e-mail',
  contactPhone: 'Kontakt telefon',
};

/** Pořadí kanonických sloupců reportu (pro stabilní pořadí sloupců v mřížce/exportu). */
export const ACQUISITION_FIELD_ORDER = Object.keys(
  ACQUISITION_FIELD_LABELS,
) as (keyof AcquisitionReportFields)[];

/** Kanonické hodnoty „Status ACQ“. */
export const ACQUISITION_STATUS = {
  UZAVRENO: 'Uzavřeno',
  NESOUHLAS: 'Nesouhlas',
  /** Výkres Akvizice → Nespecifikováno (bílá; ani uzavřeno, ani zamítnuto). */
  ZADANO: 'Zadáno',
} as const;

/**
 * Navrhovaná BUDOVA workflow podle „Status ACQ“:
 *   • Uzavřeno → VR cesta (BUDOVA.2),
 *   • Nesouhlas → BUDOVA.3 (bábovka před objektem) + BUDOVA.1 (Odmítnutý) — uživatel zvolí:
 *       odmítnutá akvizice = nelze vrtat do domu → bábovka před ním; nevyplatí-li se, úplné odmítnutí,
 *   • cokoli jiného → bez návrhu (nejednoznačné, uživatel rozhodne).
 *
 * Návrh slouží jen ke zvýraznění tlačítek; uživatel může zvolit kterékoli (přepíše workflow).
 */
export function suggestedBudovaWorkflowIdsForAcqStatus(statusAcq: string | null | undefined): string[] {
  if (isAcquisitionClosed(statusAcq)) return ['BUDOVA.2'];
  if (normalizeAcquisitionText(statusAcq) === normalizeAcquisitionText(ACQUISITION_STATUS.NESOUHLAS)) {
    return ['BUDOVA.3', 'BUDOVA.1'];
  }
  return [];
}

/** Alias reportu: „Uzavřená“ = kanonické Uzavřeno. */
const ACQUISITION_CLOSED_STATUS_ALIASES = [
  ACQUISITION_STATUS.UZAVRENO,
  'Uzavřená',
] as const;

const ACQUISITION_CLOSED_STATUS_NORMALIZED = new Set(
  ACQUISITION_CLOSED_STATUS_ALIASES.map((value) => normalizeAcquisitionText(value)),
);

/** Uzavřená akvizice (Status ACQ = Uzavřeno / Uzavřená). */
export function isAcquisitionClosed(statusAcq: string | null | undefined): boolean {
  const s = normalizeAcquisitionText(statusAcq);
  return !!s && ACQUISITION_CLOSED_STATUS_NORMALIZED.has(s);
}

/**
 * Odmítnutá / zamítnutá akvizice — kanonický Status ACQ = Nesouhlas,
 * plus aliasy (Zamítnuto / Odmítnuto / rejected) a Souhlas = Zrušená.
 */
const ACQUISITION_REJECTED_STATUS_ALIASES = [
  ACQUISITION_STATUS.NESOUHLAS,
  'Zamítnuto',
  'Zamítnutá',
  'Odmítnuto',
  'Odmítnutá',
  'rejected',
] as const;

const ACQUISITION_REJECTED_STATUS_NORMALIZED = new Set(
  ACQUISITION_REJECTED_STATUS_ALIASES.map((value) => normalizeAcquisitionText(value)),
);

export function isAcquisitionRejected(opts: {
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
}): boolean {
  const status = normalizeAcquisitionText(opts.statusAcq);
  if (status && ACQUISITION_REJECTED_STATUS_NORMALIZED.has(status)) return true;
  return (
    normalizeAcquisitionText(opts.souhlasSmlouva)
    === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.ZRUSENA)
  );
}

/**
 * Kanonické hodnoty „Souhlas / smlouva“ synchronizované ze smluv KN (`contract_type=souhlas`).
 * Excel report může mít i jiné volné texty — ty se při sync nepřepisují, pokud jsou v user_edited_fields.
 */
export const ACQUISITION_SOUHLAS_SMLUVA = {
  PRIPRAVENA: 'Připravená',
  PODEPSANA: 'Podepsaná',
  CASTECNE: 'Částečně podepsáno',
  ZRUSENA: 'Zrušená',
} as const;

export type AcquisitionSouhlasSmlouvaValue =
  (typeof ACQUISITION_SOUHLAS_SMLUVA)[keyof typeof ACQUISITION_SOUHLAS_SMLUVA];

/** Mapové / badge barvy akvizice (zelená / žlutá / červená). */
export const ACQUISITION_MAP_COLORS = {
  /** Podepsáno / Uzavřeno */
  OK: '#16a34a',
  /** Připraveno / čeká na podpis */
  PENDING: '#d97706',
  /** Částečně podepsáno */
  PARTIAL: '#ca8a04',
  /** Nesouhlas / zrušeno */
  BLOCKED: '#dc2626',
} as const;

export interface SouhlasContractStatusInput {
  status: string;
}

/**
 * Odvození `souhlas_smlouva` + navrhovaného `status_acq` z množiny smluv typu souhlas.
 * Prázdná množina → null.
 */
export function deriveAcquisitionFromSouhlasContracts(
  contracts: SouhlasContractStatusInput[],
): { souhlasSmlouva: string; statusAcq: string | null } | null {
  if (!contracts.length) return null;
  // `archivovana` = podepsaná smlouva uložená na Drive → počítá se jako podepsaná.
  const active = contracts.map((c) => {
    const s = (c.status || '').toLowerCase();
    return s === 'archivovana' ? 'podepsana' : s;
  });
  if (!active.length) return null;

  const allSigned = active.every((s) => s === 'podepsana');
  const anySigned = active.some((s) => s === 'podepsana');
  const allCancelled = active.every((s) => s === 'zrusena');
  const anyPrepared = active.some((s) => s === 'pripravena');

  if (allSigned) {
    return {
      souhlasSmlouva: ACQUISITION_SOUHLAS_SMLUVA.PODEPSANA,
      statusAcq: ACQUISITION_STATUS.UZAVRENO,
    };
  }
  if (allCancelled) {
    return {
      souhlasSmlouva: ACQUISITION_SOUHLAS_SMLUVA.ZRUSENA,
      statusAcq: ACQUISITION_STATUS.NESOUHLAS,
    };
  }
  if (anySigned && anyPrepared) {
    return {
      souhlasSmlouva: ACQUISITION_SOUHLAS_SMLUVA.CASTECNE,
      statusAcq: null,
    };
  }
  return {
    souhlasSmlouva: ACQUISITION_SOUHLAS_SMLUVA.PRIPRAVENA,
    statusAcq: null,
  };
}

/** Hex barva pro mapový ring podle status_acq / souhlas_smlouva (null = bez ringu). */
export function getAcquisitionMapColor(opts: {
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
}): string | null {
  if (isAcquisitionClosed(opts.statusAcq)) return ACQUISITION_MAP_COLORS.OK;
  const souhlas = normalizeAcquisitionText(opts.souhlasSmlouva);
  if (!souhlas && !(opts.statusAcq ?? '').trim()) return null;
  if (souhlas === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.PODEPSANA)) {
    return ACQUISITION_MAP_COLORS.OK;
  }
  if (souhlas === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.CASTECNE)) {
    return ACQUISITION_MAP_COLORS.PARTIAL;
  }
  if (
    souhlas === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.ZRUSENA) ||
    normalizeAcquisitionText(opts.statusAcq) === normalizeAcquisitionText(ACQUISITION_STATUS.NESOUHLAS)
  ) {
    return ACQUISITION_MAP_COLORS.BLOCKED;
  }
  if (souhlas === normalizeAcquisitionText(ACQUISITION_SOUHLAS_SMLUVA.PRIPRAVENA)) {
    return ACQUISITION_MAP_COLORS.PENDING;
  }
  if ((opts.statusAcq ?? '').trim()) return ACQUISITION_MAP_COLORS.PENDING;
  return null;
}

/**
 * Navrhovaný typ budovy podle „Status ACQ“.
 *
 * Akvizice ≠ typ budovy / připojení: Status ACQ (včetně Uzavřeno / Uzavřená)
 * nikdy nenavrhuje ani nemění building type. Typ volí uživatel zvlášť
 * (výkres Akvizice Podepsat/Zamítnutá, nebo ruční volba).
 */
export function suggestedBudovaBuildingTypesForAcqStatus(
  _statusAcq: string | null | undefined,
): BudovaBuildingType[] {
  return [];
}

/**
 * Výkres Akvizice (legacy): Status ACQ dříve párovaný s typem budovy.
 * Barvy na výkrese už řídí jen Status ACQ — viz `resolveAcquisitionDrawingBuildingTone`.
 */
export function akviziceDrawingStatusAcqForBuildingType(
  buildingType: string | null | undefined,
): string | null {
  const type = (buildingType ?? '').trim();
  if (type === AKVIZICE_DRAWING_SIGN_BUILDING_TYPE) return ACQUISITION_STATUS.UZAVRENO;
  if (type === AKVIZICE_DRAWING_REJECT_BUILDING_TYPE) return ACQUISITION_STATUS.NESOUHLAS;
  return null;
}

/**
 * Zelená na výkrese Akvizice: Status ACQ uzavřeno (typ budovy se nebere v úvahu).
 * `budovaBuildingType` zůstává v API kvůli zpětné kompatibilitě volajících.
 */
export function isAkviziceDrawingSignedPair(opts: {
  statusAcq?: string | null;
  budovaBuildingType?: string | null;
}): boolean {
  void opts.budovaBuildingType;
  return isAcquisitionClosed(opts.statusAcq);
}

/**
 * Černá na výkrese Akvizice: Status ACQ zamítnuto / Nesouhlas / aliasy.
 * Souhlas smlouva a typ budovy se pro barvu neberou v úvahu (bílá = vše ostatní).
 */
export function isAkviziceDrawingRejectedPair(opts: {
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
  budovaBuildingType?: string | null;
}): boolean {
  void opts.souhlasSmlouva;
  void opts.budovaBuildingType;
  return isAcquisitionRejected({ statusAcq: opts.statusAcq });
}

/**
 * Uzavřená akvizice nemění typ budovy (bábovka může zůstat BABOVKA).
 * @returns always `null` — kept for call-site compatibility / docs parity.
 */
export function budovaBuildingTypeWhenAcquisitionClosed(): BudovaBuildingType | null {
  return null;
}

/** True, když typ budovy ještě není zvolený (null / prázdný). */
export function isBudovaBuildingTypeUnset(buildingType: string | null | undefined): boolean {
  return !(buildingType ?? '').trim();
}

/** Legacy block reasons — generace se už typem budovy / Status ACQ neblokuje. */
export type AcquisitionContractGenerateBlockReason =
  | 'STATUS_ACQ_UZAVRENO'
  | 'BUILDING_TYPE_BABOVKA'
  | 'BUILDING_TYPE_ZAMITNUTA'
  | 'BUILDING_TYPE_SET';

export type AcquisitionContractGenerateGate =
  | { ok: true }
  | { ok: false; reason: AcquisitionContractGenerateBlockReason; message: string };

/**
 * Gate pro generaci akviziční smlouvy (souhlas).
 * Typ budovy ani Status ACQ = Uzavřeno generaci neblokují — smlouvy lze generovat vždy.
 * Ostatní hard gates (digitalni_cesta počet cest, KN parcely) žijí mimo tuto funkci.
 */
export function canGenerateAcquisitionContract(_opts?: {
  buildingType?: string | null;
  statusAcq?: string | null;
}): AcquisitionContractGenerateGate {
  return { ok: true };
}

/**
 * Typ kabeláže z „Způsob projednávaného technického řešení BD“:
 *   • „Vertikální a horizontální VR“ → Dedikovaná kabeláž (`true`),
 *   • „Vertikální VR“ → RISER kabeláž (`false`),
 *   • jinak → null (nezadáno).
 *
 * Ukládá se do legacy sloupce `horizontalni_propoj` (true = Dedikovaná, false = RISER).
 */
export function horizontalniPropojFromTechReseni(techReseni: string | null | undefined): boolean | null {
  const s = normalizeAcquisitionText(techReseni);
  if (!s) return null;
  if (s.includes('horizontaln')) return true;
  if (s.includes('vertikaln')) return false;
  return null;
}
